import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Block navigation when `when` is true **unless**
 * `ignore(nextLocation)` returns true.
 *
 * @param {boolean} when                – enable / disable blocking
 * @param {string}  message             – confirm-dialog text
 * @param {function} [ignore]           – (nextLocation) => boolean
 */
export function usePrompt(when, message, ignore = () => false) {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return;

    const origPush = navigator.push;
    const origReplace = navigator.replace;

    const clearSessionData = () => {
      const EDIT_SESSION_KEYS = [
        "editBookingMode",
        "editAddressMode",
        "modificationBookingId",
        "oldBooking",
        "newBooking",
        "productChanged",
        "therapistChanged",
        "addressChanged",
        "bookingToEdit",
        "state",
        "address",
        "previousPath"
      ];

      // Clear session data only when confirmed
      EDIT_SESSION_KEYS.forEach(key => sessionStorage.removeItem(key));
    };

    const confirmIfNeeded = (target, method) => {
      const pathname = typeof target === 'string' ? target : (target.pathname || '');

      if (ignore({ pathname })) {
        method(target);
        return;
      }

      // Show the confirmation prompt and clear session data only if confirmed
      if (window.confirm(message)) {
        clearSessionData();
        method(target);
      }
    };

    // Intercept navigation on the client side and handle it based on the confirm message
    navigator.push = (to, ...rest) => confirmIfNeeded(to, (t) => origPush(t, ...rest));
    navigator.replace = (to, ...rest) => confirmIfNeeded(to, (t) => origReplace(t, ...rest));

    // Cleanup the listeners when the component unmounts
    return () => {
      navigator.push = origPush;
      navigator.replace = origReplace;
    };
  }, [when, message, ignore, navigator]);
}
